import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import useAuth from "../../hooks/useAuth";
import { img, navLinks } from "../../App/Landing/landingContent";
import { Icon, IconSprite } from "./Icon";
import { ShoppingCartIcon, UserIcon, ArrowRightEndOnRectangleIcon } from "@heroicons/react/24/outline";
import { ChevronDownIcon } from "@heroicons/react/24/solid";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import UserImg from "../../assets/img/User_img.png";
import CartService from "../../Services/CartService";
import EmptyCartModal from "../../App/Account/EmptyCartModal";
import shippingStore from "../../stores/ShippingStore";

const base = process.env.REACT_APP_PublicPath;

export default function LandingHeader({ cartItemsCount }: { cartItemsCount?: number }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user, signout } = useAuth();
  const isLoggedIn = !!user?.id && user.id !== "unknown";
  const onLanding = pathname === base + "/";
  const onLogin = pathname.endsWith("/login");
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [progress, setProgress] = useState(0);
  const [cartCount, setCartCount] = useState(0);
  const [emptyCartOpen, setEmptyCartOpen] = useState(false);

  useEffect(() => {
    if (!isLoggedIn) return;
    CartService.GetCartCount()
      .then(({ CartCount }) => setCartCount(CartCount))
      .catch(console.error);
  }, [isLoggedIn]);

  useEffect(() => {
    if (cartItemsCount !== undefined) setCartCount(cartItemsCount);
  }, [cartItemsCount]);

  const showCart = async () => {
    try {
      const { CartCount } = await CartService.GetCartCount();
      setCartCount(CartCount);
      if (CartCount === 0) setEmptyCartOpen(true);
      else navigate("/yourcart");
    } catch {
      setEmptyCartOpen(true);
    }
  };

  const logOut = async () => {
    await signout();
    window.localStorage.clear();
    shippingStore.clearShippingDetails();
    navigate(base + "/login");
  };

  useEffect(() => {
    let ticking = false;
    const update = () => {
      const y = window.scrollY;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      setScrolled(y > 8);
      setProgress(max > 0 ? Math.min(y / max, 1) : 0);
      ticking = false;
    };
    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    update();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Section anchors only resolve on the landing page itself
  const sectionHref = (hash: string) => (onLanding ? hash : base + "/" + hash);
  const startOrder = () => navigate(base + (isLoggedIn ? "/home" : "/login"));

  const accountPath = base + "/myaccounts";
  const onAccount = [accountPath, base + "/orders"].includes(pathname.toLowerCase());

  const authLink = onLogin
    ? { to: base + "/registration", label: "SIGN UP" }
    : { to: base + "/login", label: "SIGN IN" };

  return (
    <>
    <IconSprite />
    <header
      className={`sticky top-0 z-50 py-2 bg-white/95 backdrop-blur border-b border-slate-100 transition-shadow duration-300 ${
        scrolled ? "shadow-[0_8px_28px_rgba(11,27,63,.08)]" : ""
      }`}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-0 h-16 flex items-center justify-between gap-6">
        <a href={onLanding ? "#top" : base + "/"} className="inline-block" aria-label="BudgetPrintPros home">
          <img src={img("Logo_B.svg")} alt="BudgetPrintPros" className="h-7 w-auto sm:h-8" />
        </a>

        <nav className="hidden lg:flex items-center gap-8 text-sm font-bold text-slate-700 uppercase" aria-label="Primary">
          {navLinks.map((l) => (
            <a key={l.href} href={sectionHref(l.href)} className="hover:text-brand">{l.name}</a>
          ))}
          {isLoggedIn && (
            <Link
              to={accountPath}
              aria-current={onAccount ? "page" : undefined}
              className={`border-b-2 py-1 ${onAccount ? "border-brand text-brand" : "border-transparent hover:text-brand"}`}
            >
              MY ACCOUNT
            </Link>
          )}
        </nav>

        <div className="flex items-center gap-2">
          {isLoggedIn && (
            <>
              <button
                type="button"
                onClick={showCart}
                className="relative inline-flex h-10 w-10 items-center justify-center rounded-full text-ink hover:text-brand"
                aria-label="Your cart"
              >
                <ShoppingCartIcon className="h-5 w-5" />
                {cartCount > 0 && (
                  <span className="absolute -right-0.5 -top-0.5 flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
                    {cartCount}
                  </span>
                )}
              </button>
              <div className="mx-2 hidden h-10 w-px bg-slate-200 sm:block" aria-hidden="true" />
              <Menu as="div" className="relative">
                <MenuButton className="flex items-center gap-3 rounded-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand">
                  <span className="hidden text-sm text-slate-500 sm:inline">
                    Hello, <span className="font-bold text-ink">{user?.firstName} {user?.lastName}</span>
                  </span>
                  <img src={UserImg} alt="" className="h-8 w-8 rounded-full object-cover" />
                  <ChevronDownIcon className="h-4 w-4 text-ink" />
                </MenuButton>
                <MenuItems className="absolute right-0 mt-3 w-48 rounded-xl border border-slate-100 bg-white py-2 shadow-[0_8px_28px_rgba(11,27,63,.12)] focus:outline-none">
                  <MenuItem>
                    <button
                      type="button"
                      onClick={() => navigate(accountPath)}
                      className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm font-medium text-slate-700 data-[focus]:bg-brand-soft data-[focus]:text-brand"
                    >
                      <UserIcon className="h-4 w-4" /> My Account
                    </button>
                  </MenuItem>
                  <MenuItem>
                    <button
                      type="button"
                      onClick={logOut}
                      className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm font-medium text-slate-700 data-[focus]:bg-brand-soft data-[focus]:text-brand"
                    >
                      <ArrowRightEndOnRectangleIcon className="h-4 w-4" /> Log Out
                    </button>
                  </MenuItem>
                </MenuItems>
              </Menu>
            </>
          )}
          {!isLoggedIn && <Link
            to={authLink.to}
            className="hidden sm:inline-flex items-center gap-2 rounded-full border border-brand px-5 py-2.5 text-base font-semibold text-brand shadow-sm hover:bg-brand-dark hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
          >
            {authLink.label} <Icon name="arrow" className="h-4 w-4" />
          </Link>}
          <button
            type="button"
            onClick={() => setMenuOpen((o) => !o)}
            className="lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-lg border border-slate-200"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            aria-controls="mobileNav"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d={menuOpen ? "M6 6l12 12M18 6L6 18" : "M4 7h16M4 12h16M4 17h16"} />
            </svg>
          </button>
        </div>
      </div>

      <nav id="mobileNav" className={`${menuOpen ? "" : "hidden"} lg:hidden border-t border-slate-100 bg-white px-4 pb-4`} aria-label="Mobile">
        <ul className="flex flex-col py-2 text-base font-medium uppercase">
          {navLinks.map((l, i) => (
            <li key={l.href}>
              <a
                className={`block py-3 ${i < navLinks.length - 1 ? "border-b border-slate-100" : ""}`}
                href={sectionHref(l.href)}
                onClick={() => setMenuOpen(false)}
              >
                {l.name}
              </a>
            </li>
          ))}
          {isLoggedIn && (
            <li>
              <Link
                to={accountPath}
                className={`block border-t border-slate-100 py-3 ${onAccount ? "text-brand" : ""}`}
                onClick={() => setMenuOpen(false)}
              >
                MY ACCOUNT
              </Link>
            </li>
          )}
        </ul>
        <button
          type="button"
          onClick={() => { setMenuOpen(false); startOrder(); }}
          className="mt-2 flex w-full items-center justify-center gap-2 rounded-lg bg-brand px-5 py-3 text-sm font-semibold text-white"
        >
          Start Your Order <Icon name="arrow" className="h-4 w-4" />
        </button>
      </nav>

      <div
        className="absolute bottom-0 left-0 h-0.5 w-full origin-left bg-brand"
        style={{ transform: `scaleX(${progress})` }}
        aria-hidden="true"
      />
    </header>
    <EmptyCartModal isOpen={emptyCartOpen} onClose={() => setEmptyCartOpen(false)} />
    </>
  );
}
